/**
 * Publish Flow file download helpers.
 *
 * Uses explicit bucket/key pointers from flow metrics — never infers account
 * identity from S3 key prefixes.
 */

export type PublishFlowFileRole = 'sourceInput' | 'preProcessorOutput' | 'pipelineInput'

export type PublishFlowFilePointer = {
  bucket: string
  key: string
}

export type PublishFlowFilePresignResponse = {
  presignedUrl: string
  bucket?: string
  key?: string
  fileName?: string
}

export class PublishFlowFilePresignError extends Error {
  readonly code: string

  constructor(code: string, message: string) {
    super(message)
    this.name = 'PublishFlowFilePresignError'
    this.code = code
  }
}

export function isPublishFlowFilePointer(value: unknown): value is PublishFlowFilePointer {
  if (!value || typeof value !== 'object') return false
  const record = value as Record<string, unknown>
  return (
    typeof record.bucket === 'string' &&
    record.bucket.trim().length > 0 &&
    typeof record.key === 'string' &&
    record.key.trim().length > 0
  )
}

export function preProcessorSourceFilePointer(row: {
  sourceFile?: { bucket?: string; key?: string } | null
}): PublishFlowFilePointer | null {
  const bucket = String(row.sourceFile?.bucket ?? '').trim()
  const key = String(row.sourceFile?.key ?? '').trim()
  if (!bucket || !key) return null
  return { bucket, key }
}

export function resolvePreProcessorOutputFilePointer(input: {
  preProcessorOutputFile?: PublishFlowFilePointer | null
  runSummaryFileKey?: string | null
  accountJazelId: string
}): PublishFlowFilePointer | null {
  if (isPublishFlowFilePointer(input.preProcessorOutputFile)) {
    return input.preProcessorOutputFile
  }

  const key = String(input.runSummaryFileKey ?? '').trim()
  const accountJazelId = String(input.accountJazelId ?? '').trim()
  if (!key || !accountJazelId) return null
  if (!key.startsWith(`${accountJazelId}/`)) return null

  return { bucket: '', key }
}

export function publishFlowFileUnavailableMessage(role: PublishFlowFileRole): string {
  switch (role) {
    case 'preProcessorOutput':
    case 'pipelineInput':
      return 'Output file unavailable'
    case 'sourceInput':
    default:
      return 'Source file unavailable'
  }
}

export function publishFlowFileMissingMessage(role: PublishFlowFileRole): string {
  switch (role) {
    case 'preProcessorOutput':
    case 'pipelineInput':
      return 'Output file is no longer available.'
    case 'sourceInput':
    default:
      return 'Source file is no longer available.'
  }
}

function parsePresignErrorBody(body: unknown): { code?: string; message?: string; error?: string } {
  if (!body || typeof body !== 'object') return {}
  const record = body as Record<string, unknown>
  return {
    code: typeof record.error === 'string' ? record.error : undefined,
    message: typeof record.message === 'string' ? record.message : undefined,
    error: typeof record.error === 'string' ? record.error : undefined,
  }
}

export async function requestPublishFlowFilePresign(params: {
  endpoint: string
  jazelId: string
  correlationId?: string
  pointer: PublishFlowFilePointer
  fileRole: PublishFlowFileRole
  sessionToken?: string
  expiresIn?: number
}): Promise<string> {
  const qs = new URLSearchParams({
    jazelId: params.jazelId,
    key: params.pointer.key,
    fileRole: params.fileRole,
    expiresIn: String(params.expiresIn ?? 900),
  })
  if (params.pointer.bucket.trim().length > 0) {
    qs.set('bucket', params.pointer.bucket.trim())
  }
  if (params.correlationId?.trim()) {
    qs.set('correlationId', params.correlationId.trim())
  }

  const headers: Record<string, string> = {}
  if (params.sessionToken) {
    headers.Authorization = `Bearer ${params.sessionToken}`
  }

  const res = await fetch(`${params.endpoint}/reports/import-feed-file-presign?${qs.toString()}`, {
    method: 'GET',
    headers,
  })

  let body: unknown = null
  try {
    body = await res.json()
  } catch {
    body = null
  }

  if (!res.ok) {
    const parsed = parsePresignErrorBody(body)
    const code = parsed.code ?? 'PRESIGN_FAILED'
    const message =
      parsed.message ??
      (typeof parsed.error === 'string' && parsed.error !== code ? parsed.error : null) ??
      (res.status === 403 && params.fileRole === 'sourceInput'
        ? 'Source file is not available for this run.'
        : publishFlowFileMissingMessage(params.fileRole))
    throw new PublishFlowFilePresignError(code, message)
  }

  const presignedUrl = (body as PublishFlowFilePresignResponse | null)?.presignedUrl
  if (typeof presignedUrl !== 'string' || presignedUrl.trim().length === 0) {
    throw new PublishFlowFilePresignError(
      'PRESIGN_FAILED',
      publishFlowFileMissingMessage(params.fileRole),
    )
  }

  return presignedUrl
}

export function publishFlowFileCacheKey(pointer: PublishFlowFilePointer, role: PublishFlowFileRole): string {
  return `${role}:${pointer.bucket}:${pointer.key}`
}

/**
 * Open a presigned download URL without navigating the current page to the
 * presign API endpoint.
 */
export function openExternalDownloadUrl(url: string): void {
  const trimmed = url.trim()
  if (trimmed.length === 0) return

  const anchor = document.createElement('a')
  anchor.href = trimmed
  anchor.target = '_blank'
  anchor.rel = 'noopener noreferrer'
  anchor.style.display = 'none'
  document.body.appendChild(anchor)
  anchor.click()
  document.body.removeChild(anchor)
}
